"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Lock, CloudUpload, CircleCheck, Loader, Calendar, MapPin, Clock, Music, FileText, User, Mail } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
const commonInputClasses = "flex w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 border-input placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground md:text-sm";
function EventEntryFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-serif tracking-wide text-foreground">Submission Successful</h3>
        <p className="text-muted-foreground max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary/50 hover:bg-primary/10">
          Submit Another Event
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <div className="space-y-6 md:col-span-2 p-6 rounded-xl bg-background/40 border border-border/50">
          <h4 className="text-sm font-semibold text-primary uppercase tracking-wider flex items-center gap-2 mb-4">
            <User className="size-4" /> Submitter Details
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField control={control} name="staff_name" rules={{
            required: "Staff name is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Your Name</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                      <Input placeholder="Jane Smith" className="pl-9" {...field} />
                    </div>
                  </FormControl>
                  <FormDescription>Name of the staff member submitting the event.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="staff_email" rules={{
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Your Email</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                      <Input type="email" placeholder="jane.smith@elboroom.com" className="pl-9" {...field} />
                    </div>
                  </FormControl>
                  <FormDescription>We'll use this to confirm the submission.</FormDescription>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>

        {}
        <div className="space-y-6 md:col-span-2 p-6 rounded-xl bg-background/40 border border-border/50">
          <h4 className="text-sm font-semibold text-primary uppercase tracking-wider flex items-center gap-2 mb-4">
            <SafeHydrate><Calendar className="size-4" /></SafeHydrate> Event Information
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField control={control} name="event_title" rules={{
            required: "Event title is required"
          }} render={({
            field
          }) => <FormItem className="md:col-span-2">
                  <FormLabel>Event Title</FormLabel>
                  <FormControl>
                    <Input placeholder="Sunset Live Band Performance" {...field} />
                  </FormControl>
                  <FormDescription>The headline name of the event.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="event_date" rules={{
            required: "Event date is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Event Date</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <SafeHydrate><Calendar className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" /></SafeHydrate>
                      <Input type="date" className="pl-9" {...field} />
                    </div>
                  </FormControl>
                  <FormDescription>Select the calendar date of the event.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="start_time" rules={{
            required: "Start time is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Start Time</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
                      <Input type="time" placeholder="18:00" className="pl-9" {...field} />
                    </div>
                  </FormControl>
                  <FormDescription>Use 24-hour format (HH:MM).</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="location" rules={{
            required: "Location is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Location</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                      <Input placeholder="Elbo Room Main Stage" className="pl-9" {...field} />
                    </div>
                  </FormControl>
                  <FormDescription>Venue or area within the Elbo Room.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="band" rules={{
            required: "Please select a band"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Band / Show</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Music className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
                      <select className={`${commonInputClasses} h-9 pl-9 appearance-none`} {...field}>
                        <option value="" disabled>Select a band</option>
                        <option value="Band A">Band A</option>
                        <option value="Band B">Band B</option>
                        <option value="Band C">Band C</option>
                      </select>
                    </div>
                  </FormControl>
                  <FormDescription>Choose the daily band scheduled for this event.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="description" render={({
            field
          }) => <FormItem className="md:col-span-2">
                  <FormLabel>Event Description</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <FileText className="absolute left-3 top-3 size-4 text-muted-foreground" />
                      <textarea placeholder="Brief details about the event, special guests, etc." className={`${commonInputClasses} min-h-[100px] pl-9 py-2 resize-y`} {...field} />
                    </div>
                  </FormControl>
                  <FormDescription>Optional – additional information for the news engine.</FormDescription>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>

        {}
        <div className="md:col-span-2">
          <FormLabel className="mb-2 block">Quick Upload (Band Photos)</FormLabel>
          <div className="border-2 border-dashed border-border/50 hover:border-primary/50 transition-colors rounded-xl p-8 text-center bg-background/20 cursor-pointer group">
            <div className="size-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <CloudUpload className="size-6 text-primary" />
            </div>
            <p className="text-sm font-medium text-foreground mb-1">Drag and drop photos here</p>
            <p className="text-xs text-muted-foreground">or click to browse files (JPG, PNG up to 5MB)</p>
          </div>
        </div>
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
          <div className="size-5 rounded-full bg-destructive/20 flex items-center justify-center shrink-0 mt-0.5">
            <span className="text-destructive text-xs font-bold">!</span>
          </div>
          <p className="text-sm text-destructive-foreground">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-semibold tracking-wide shadow-[0_0_20px_rgba(var(--primary),0.3)] hover:shadow-[0_0_30px_rgba(var(--primary),0.5)] transition-shadow">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing Entry...
          </> : 'Submit Event to Engine'}
      </Button>
    </Form>;
}
export default function StaffPortalEventEntry() {
  return <section id="staff-portal-event-entry" className="dark bg-background text-foreground py-24 relative overflow-hidden min-h-screen flex items-center">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[30rem] h-[30rem] bg-secondary/10 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-[0.03] pointer-events-none mix-blend-overlay" />

      <div className="container max-w-4xl mx-auto px-4 relative z-10">
        <div className="flex flex-col items-center text-center mb-10">
          <Badge variant="outline" className="border-primary text-primary mb-4 px-3 py-1 bg-primary/5 backdrop-blur-sm">
            <Lock className="size-3 mr-2 inline-block" />
            Secure Staff Portal
          </Badge>
          <h2 className="text-4xl md:text-5xl font-serif tracking-wider text-foreground mb-4 drop-shadow-md">
            Event Entry <span className="text-primary">Hub</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl text-lg">
            Authorized personnel only. Submit daily live shows, band schedules, and special events directly to the Elbo Room news engine.
          </p>
        </div>

        <div className="bg-card/40 backdrop-blur-xl border border-border/50 shadow-2xl shadow-black/50 rounded-2xl p-6 md:p-10 relative overflow-hidden">
          {}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
          
          <FormProvider formId="event_entry_form_elbo_room_hub" sectionName="Staff Portal Event Entry" defaultValues={{
          staff_name: "",
          staff_email: "",
          event_title: "",
          event_date: "",
          start_time: "",
          location: "",
          band: "",
          description: ""
        }} validationRules={{
          staff_name: {
            required: true
          },
          staff_email: {
            required: true,
            pattern: "email"
          },
          event_title: {
            required: true
          },
          event_date: {
            required: true
          },
          start_time: {
            required: true
          },
          location: {
            required: true
          },
          band: {
            required: true
          }
        }} submitText="Submit Event to Engine" successMessage="Event data has been securely transmitted and queued for publication." className="w-full">
            <EventEntryFormContent />
          </FormProvider>
        </div>
      </div>
    </section>;
}